import React, {
  useMemo,
  forwardRef,
  useRef,
  CSSProperties,
  useImperativeHandle,
  Context,
} from 'react'
import classnames from 'classnames'
import { AmplitudeContext, useInputTypingEventTracking, ContextType } from '@platform/utils'

import { AlertCircleIcon } from '../../assets/icons/icons'
import Button, { ButtonVariant } from '../button/button'
import IconButton, { IconButtonVariant } from '../icon-button/icon-button'

import styles from './input.module.scss'

const INPUT_PADDING = 12
const MINIMUM_FIXED_WIDTH = 32

type Data = {
  [key: string]: unknown
}

type LabelledActionButton = {
  onClick: React.MouseEventHandler<HTMLButtonElement>
  isIconButton?: false
  label: string
  variant?: ButtonVariant
  data?: Data
  icon?: React.ElementType
}

type IconActionButton = {
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  isIconButton?: true
  label: string
  variant?: IconButtonVariant
  data?: Data
  icon: React.ElementType
}

type BaseProps = {
  label: string
  id: string
  size?: 'small' | 'medium'
  helperText?: string
  error?: string
  // TODO: Fix typing to accept either prefix or startAdornment
  prefix?: string
  startAdornment?: string | React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  // TODO: Fix typing to accept either suffix or actionButton
  suffix?: string
  actionButton?: LabelledActionButton | IconActionButton | null
  placeholder?: string
  optionalText?: string
  type?: string
  disabled?: boolean
  hiddenLabel?: boolean
  hasError?: boolean
  className?: string
  inputClassName?: string
  actionButtonClassName?: string
  startAdornmentClassName?: string
  mask?: string
  trackingIdentifier?: string
}

export type BaseInputProps = BaseProps

export type InputProps = BaseInputProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof BaseInputProps>

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      id,
      placeholder,
      optionalText,
      startAdornment,
      prefix,
      suffix,
      actionButton,
      size = 'medium',
      helperText = '',
      error,
      disabled = false,
      className = '',
      inputClassName = '',
      actionButtonClassName = '',
      startAdornmentClassName = '',
      hiddenLabel = false,
      hasError,
      trackingIdentifier,
      ...restProps
    }: InputProps,
    ref
  ) => {
    const prefixRef = useRef<HTMLSpanElement>(null)
    const suffixRef = useRef<HTMLSpanElement>(null)

    const innerRef = useRef<HTMLInputElement | null>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)

    useInputTypingEventTracking(
      innerRef,
      AmplitudeContext as unknown as Context<ContextType>,
      'input_change',
      {
        id: trackingIdentifier,
        label,
      }
    )

    const supportText = error || helperText
    const buttonTrackingIdentifier = trackingIdentifier && `${trackingIdentifier} input button`

    const inputClasses = classnames(inputClassName, styles['input'], {
      [styles['button-input-label']]: actionButton && actionButton.label,
      [styles['button-input-icon']]: actionButton && actionButton.icon,
      [styles['input-optional-icon']]: startAdornment,
    })

    const hasActionButton = actionButton && (actionButton.label || actionButton.icon)
    const hasActionIconButton = actionButton && actionButton.isIconButton
    const renderActionButton = useMemo(() => {
      if (hasActionIconButton) {
        return (
          <IconButton
            disabled={disabled}
            className={classnames(styles['optional-button'], actionButtonClassName)}
            onClick={actionButton.onClick}
            type="button"
            size={size}
            variant={actionButton.variant}
            icon={actionButton.icon}
            label={actionButton.label}
            trackingIdentifier={buttonTrackingIdentifier}
            {...actionButton.data}
          />
        )
      }

      if (hasActionButton) {
        return (
          <Button
            disabled={disabled}
            className={classnames(styles['optional-button'], actionButtonClassName)}
            onClick={actionButton.onClick}
            type="button"
            size={size}
            variant={actionButton.variant}
            startAdornment={actionButton.icon}
            trackingIdentifier={buttonTrackingIdentifier}
            {...actionButton.data}
          >
            {actionButton.label}
          </Button>
        )
      }

      if (suffix) {
        return (
          <span className={styles.suffix} ref={suffixRef}>
            {suffix}
          </span>
        )
      }
      return null
    }, [
      hasActionIconButton,
      hasActionButton,
      suffix,
      disabled,
      actionButtonClassName,
      actionButton,
      size,
      buttonTrackingIdentifier,
    ])

    const renderOptional = useMemo(() => {
      return (
        optionalText && (
          <p className={classnames(styles['optional'], styles['optional-text'])}>{optionalText}</p>
        )
      )
    }, [optionalText])

    const renderLeftAdornment = useMemo(() => {
      if (prefix) {
        return (
          <span className={styles.prefix} ref={prefixRef}>
            {prefix}
          </span>
        )
      }

      const StartAdornment = startAdornment as React.ElementType
      return (
        startAdornment && (
          <StartAdornment
            className={classnames(styles['optional-icon'], startAdornmentClassName)}
          />
        )
      )
    }, [prefix, startAdornment, startAdornmentClassName])

    const renderStyle = () => {
      const style: CSSProperties = {}

      if (prefix) {
        const prefixElement = prefixRef?.current
        const prefixWidth = prefixElement
          ? prefixElement?.offsetLeft + INPUT_PADDING + prefixElement?.offsetWidth
          : MINIMUM_FIXED_WIDTH

        style.paddingLeft = prefixWidth
      }

      if (suffix) {
        const suffixElement = suffixRef?.current
        const suffixWidth = suffixElement
          ? 2 * INPUT_PADDING + suffixElement?.offsetWidth
          : MINIMUM_FIXED_WIDTH

        style.paddingRight = suffixWidth
      }
      return style
    }

    return (
      <div
        className={classnames(
          styles['input-wrapper'],
          {
            [styles['disabled-input']]: disabled,
            [styles['has-label']]: (label && !hiddenLabel) || optionalText,
            [styles['has-support-text']]: supportText,
          },
          className
        )}
      >
        <label
          htmlFor={id}
          className={classnames(styles['label'], { [styles['visually-hidden']]: hiddenLabel })}
        >
          {label}
        </label>
        {renderOptional}
        <div
          className={classnames(styles['input-container'], styles[size], {
            [styles['input-error']]: hasError || !!error,
          })}
        >
          <input
            disabled={disabled}
            aria-describedby={supportText ? `${id}helperText` : ''}
            id={id}
            className={inputClasses}
            placeholder={placeholder}
            ref={innerRef as React.Ref<HTMLInputElement>}
            style={renderStyle()}
            {...restProps}
          />
          {renderLeftAdornment}
          {renderActionButton}
        </div>
        {(!!helperText || !!error) && (
          <p
            id={`${id}helperText`}
            className={classnames(styles['helper-text'], {
              [styles['helper-text-error']]: !!error,
            })}
          >
            {!!error && <AlertCircleIcon className={styles['icon']} />} {supportText}
          </p>
        )}
      </div>
    )
  }
)

export default Input
